import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faCode, faMusic, faDumbbell, faUtensils, faGlobe, faLightbulb, faRobot, faBrain } from '@fortawesome/free-solid-svg-icons';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const background = `/assets/img/background.jpg`;

const useStyles = makeStyles((theme) => ({
  section: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: `
      radial-gradient(circle at 25% 25%, rgba(0, 245, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(199, 125, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(57, 255, 20, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, #0a0a0f 0%, #151521 50%, #1a1a2e 100%),
      url(${background})
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    color: '#fff',
    padding: theme.spacing(10, 0),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(45deg, rgba(0, 245, 255, 0.03), rgba(199, 125, 255, 0.03))',
      animation: 'backgroundShift 20s ease-in-out infinite',
      zIndex: 1,
    },
  },
  container: {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    position: 'relative',
    zIndex: 2,
  },
  titleHeader: {
    textAlign: 'center',
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 700,
    fontSize: '3rem',
    background: 'linear-gradient(135deg, #00f5ff, #c77dff, #e0aaff)',
    backgroundSize: '300% 300%',
    animation: 'gradientShift 4s ease infinite',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(6),
    position: 'relative',
    '&::after': {
      content: '"🎯"',
      position: 'absolute',
      top: '-15px',
      right: '-50px',
      fontSize: '2rem',
      opacity: 0.8,
    },
    '&::before': {
      content: '"⭐"',
      position: 'absolute',
      top: '-15px',
      left: '-50px',
      fontSize: '2rem',
      opacity: 0.8,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  gridContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    background: 'rgba(10, 15, 30, 0.8)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '20px',
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: 'var(--hobby-gradient, linear-gradient(90deg, transparent, #00f5ff, transparent))',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      transform: 'translateY(-10px) scale(1.03)',
      borderColor: 'var(--hobby-color, #00f5ff)',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), 0 0 30px var(--hobby-glow, rgba(0, 245, 255, 0.3))',
      background: 'rgba(15, 20, 35, 0.9)',
      '&::before': {
        opacity: 1,
      },
      '& $icon': {
        transform: 'scale(1.2) rotate(10deg)',
        color: 'var(--hobby-color, #00f5ff)',
      },
      '& $cardTitle': {
        color: 'var(--hobby-color, #00f5ff)',
        textShadow: '0 0 10px var(--hobby-color, #00f5ff)',
      },
    },
  },
  icon: {
    fontSize: '3rem',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s ease',
  },
  cardTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    fontFamily: '"Inter", sans-serif',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: theme.spacing(1),
    transition: 'all 0.3s ease',
  },
  cardText: {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: '"Inter", sans-serif',
    flex: '1',
    display: 'flex',
    alignItems: 'center',
  },
  // Animaciones
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '@keyframes backgroundShift': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.8 },
  },
}));

const hobbies = [
  {
    icon: faRobot,
    key: 'ai',
    color: '#ff1744',
    glow: 'rgba(255, 23, 68, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #ff1744, transparent)',
  },
  {
    icon: faCode,
    key: 'coding',
    color: '#00f5ff',
    glow: 'rgba(0, 245, 255, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #00f5ff, transparent)',
  },
  {
    icon: faLightbulb,
    key: 'learning',
    color: '#c77dff',
    glow: 'rgba(199, 125, 255, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #c77dff, transparent)',
  },
  {
    icon: faBrain,
    key: 'problemSolving',
    color: '#e0aaff',
    glow: 'rgba(224, 170, 255, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #e0aaff, transparent)',
  },
  {
    icon: faBook,
    key: 'reading',
    color: '#39ff14',
    glow: 'rgba(57, 255, 20, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #39ff14, transparent)',
  },
  {
    icon: faMusic,
    key: 'music',
    color: '#ff6b35',
    glow: 'rgba(255, 107, 53, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #ff6b35, transparent)',
  },
  {
    icon: faUtensils,
    key: 'cooking',
    color: '#ffd23f',
    glow: 'rgba(255, 210, 63, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #ffd23f, transparent)',
  },
  {
    icon: faDumbbell,
    key: 'fitness',
    color: '#4ecdc4',
    glow: 'rgba(78, 205, 196, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #4ecdc4, transparent)',
  },
  {
    icon: faGlobe,
    key: 'travel',
    color: '#45b7d1',
    glow: 'rgba(69, 183, 209, 0.3)',
    gradient: 'linear-gradient(90deg, transparent, #45b7d1, transparent)',
  },
];

const HobbiesInterests = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <section className={classes.section}>
      <div className={classes.container}>
        <Typography variant="h1" className={classes.titleHeader}>
          {t('hobbies.title')}
        </Typography>
        
        <Grid container spacing={4} className={classes.gridContainer}>
          {hobbies.map((hobby, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <div 
                className={classes.card}
                style={{
                  '--hobby-color': hobby.color,
                  '--hobby-glow': hobby.glow,
                  '--hobby-gradient': hobby.gradient,
                }}
              >
                <FontAwesomeIcon 
                  icon={hobby.icon} 
                  className={classes.icon}
                />
                <Typography variant="h5" className={classes.cardTitle}>
                    {t(`hobbies.items.${hobby.key}.title`)}
                </Typography>
                <Typography variant="body1" className={classes.cardText}>
                    {t(`hobbies.items.${hobby.key}.description`)}
                </Typography>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    </section>
  );
};

export default HobbiesInterests;
